import { useMemo, useCallback } from 'react';
import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';

import {
  ExternalLoyaltyManualOfferFilter,
  External_Loyalty_Manual_Offer_Manufacturer,
  External_Loyalty_Manual_Offer_Status,
} from '@occc/graphql';

import { OfferTableType, OffersFilterValues } from '../tobacco-api-offers.types';
import { DEFAULT_FILTER_VALUES } from '../tobacco-api-offers.constants';

const IS_ERRORED_OPTIONS = ['all', 'yes', 'no'] as const;
const MANUFACTURER_OPTIONS: readonly string[] = ['', ...Object.values(External_Loyalty_Manual_Offer_Manufacturer)];
const STATUS_OPTIONS: readonly string[] = ['', ...Object.values(External_Loyalty_Manual_Offer_Status)];

const parsers = {
  brand: parseAsString.withDefault(DEFAULT_FILTER_VALUES.brand),
  manufacturer: parseAsStringLiteral(MANUFACTURER_OPTIONS).withDefault(DEFAULT_FILTER_VALUES.manufacturer),
  states: parseAsArrayOf(parseAsString).withDefault(DEFAULT_FILTER_VALUES.states),
  runDatesStart: parseAsString.withDefault(DEFAULT_FILTER_VALUES.runDatesStart),
  runDatesEnd: parseAsString.withDefault(DEFAULT_FILTER_VALUES.runDatesEnd),
  isErrored: parseAsStringLiteral(IS_ERRORED_OPTIONS).withDefault(DEFAULT_FILTER_VALUES.isErrored),
  status: parseAsStringLiteral(STATUS_OPTIONS).withDefault(DEFAULT_FILTER_VALUES.status),
  name: parseAsString.withDefault(DEFAULT_FILTER_VALUES.name),
  externalCampaignId: parseAsString.withDefault(DEFAULT_FILTER_VALUES.externalCampaignId),
  retailerNetworkUuid: parseAsString.withDefault(DEFAULT_FILTER_VALUES.retailerNetworkUuid),
  retailerNetworkName: parseAsString.withDefault(DEFAULT_FILTER_VALUES.retailerNetworkName),
  paginationAfter: parseAsString,
  paginationBefore: parseAsString,
};

const toUrlKeys = (tableType: OfferTableType) => ({
  brand: `${tableType}Brand`,
  manufacturer: `${tableType}Manufacturer`,
  states: `${tableType}States`,
  runDatesStart: `${tableType}RunDatesStart`,
  runDatesEnd: `${tableType}RunDatesEnd`,
  isErrored: `${tableType}IsErrored`,
  status: `${tableType}Status`,
  name: `${tableType}Name`,
  externalCampaignId: `${tableType}ExternalCampaignId`,
  retailerNetworkUuid: `${tableType}RetailerNetworkUuid`,
  retailerNetworkName: `${tableType}RetailerNetworkName`,
  paginationAfter: `${tableType}After`,
  paginationBefore: `${tableType}Before`,
});

const isFilterActive = (filterValues: OffersFilterValues): boolean =>
  filterValues.brand !== DEFAULT_FILTER_VALUES.brand ||
  filterValues.manufacturer !== DEFAULT_FILTER_VALUES.manufacturer ||
  filterValues.states.length > 0 ||
  filterValues.runDatesStart !== DEFAULT_FILTER_VALUES.runDatesStart ||
  filterValues.runDatesEnd !== DEFAULT_FILTER_VALUES.runDatesEnd ||
  filterValues.isErrored !== DEFAULT_FILTER_VALUES.isErrored ||
  filterValues.status !== DEFAULT_FILTER_VALUES.status ||
  filterValues.name !== DEFAULT_FILTER_VALUES.name ||
  filterValues.externalCampaignId !== DEFAULT_FILTER_VALUES.externalCampaignId ||
  filterValues.retailerNetworkUuid !== DEFAULT_FILTER_VALUES.retailerNetworkUuid;

const toFilterInput = (values: OffersFilterValues): ExternalLoyaltyManualOfferFilter | undefined => {
  if (!isFilterActive(values)) return undefined;

  const filter: ExternalLoyaltyManualOfferFilter = {};

  if (values.brand) filter.brandEquals = values.brand;
  if (values.manufacturer) {
    filter.manufacturerEquals = values.manufacturer as External_Loyalty_Manual_Offer_Manufacturer;
  }
  if (values.states.length > 0) filter.statesFilter = values.states;
  if (values.runDatesStart) filter.startDateAtMin = values.runDatesStart;
  if (values.runDatesEnd) filter.endDateAtMax = values.runDatesEnd;
  if (values.isErrored === 'yes') filter.hasError = true;
  if (values.isErrored === 'no') filter.hasError = false;
  if (values.status) filter.statusEquals = values.status as External_Loyalty_Manual_Offer_Status;
  if (values.name) filter.nameContains = values.name;
  if (values.externalCampaignId) filter.externalCampaignIdContains = values.externalCampaignId;
  if (values.retailerNetworkUuid) filter.retailerNetworkUuidEquals = values.retailerNetworkUuid;

  return filter;
};

type UseOffersFilterResult = {
  filterValues: OffersFilterValues;
  filterInput: ExternalLoyaltyManualOfferFilter | undefined;
  isFiltered: boolean;
  onFilterApply: (values: OffersFilterValues) => void;
  onFilterReset: () => void;
};

export const useOffersFilter = (tableType: OfferTableType): UseOffersFilterResult => {
  const [rawFilterValues, setFilterValues] = useQueryStates(parsers, {
    urlKeys: toUrlKeys(tableType),
  });

  // The uuid/name pair is only valid together — an orphaned half (e.g. from a
  // hand-edited URL) either renders a selection with no active filter, or an
  // active filter with no visible selection. Treat a partial pair as no selection.
  const filterValues = useMemo<OffersFilterValues>(() => {
    const hasValidRetailerNetworkSelection =
      !!rawFilterValues.retailerNetworkUuid && !!rawFilterValues.retailerNetworkName;
    return {
      ...rawFilterValues,
      retailerNetworkUuid: hasValidRetailerNetworkSelection
        ? rawFilterValues.retailerNetworkUuid
        : DEFAULT_FILTER_VALUES.retailerNetworkUuid,
      retailerNetworkName: hasValidRetailerNetworkSelection
        ? rawFilterValues.retailerNetworkName
        : DEFAULT_FILTER_VALUES.retailerNetworkName,
    };
  }, [rawFilterValues]);

  const filterInput = useMemo(() => toFilterInput(filterValues), [filterValues]);

  const isFiltered = isFilterActive(filterValues);

  const resetPagination = { paginationAfter: null, paginationBefore: null };

  const onFilterApply = useCallback(
    (values: OffersFilterValues) => {
      const hasValidRetailerNetworkSelection =
        !!values.retailerNetworkUuid && !!values.retailerNetworkName;
      setFilterValues({
        brand: values.brand || null,
        manufacturer: values.manufacturer || null,
        states: values.states.length > 0 ? values.states : null,
        runDatesStart: values.runDatesStart || null,
        runDatesEnd: values.runDatesEnd || null,
        isErrored: values.isErrored !== DEFAULT_FILTER_VALUES.isErrored ? values.isErrored : null,
        status: values.status || null,
        name: values.name || null,
        externalCampaignId: values.externalCampaignId || null,
        retailerNetworkUuid: hasValidRetailerNetworkSelection ? values.retailerNetworkUuid : null,
        retailerNetworkName: hasValidRetailerNetworkSelection ? values.retailerNetworkName : null,
        ...resetPagination,
      });
    },
    [setFilterValues],
  );

  const onFilterReset = useCallback(() => {
    setFilterValues({
      brand: null,
      manufacturer: null,
      states: null,
      runDatesStart: null,
      runDatesEnd: null,
      isErrored: null,
      status: null,
      name: null,
      externalCampaignId: null,
      retailerNetworkUuid: null,
      retailerNetworkName: null,
      ...resetPagination,
    });
  }, [setFilterValues]);

  return { filterValues, filterInput, isFiltered, onFilterApply, onFilterReset };
};
